<script>
/**
 * 관리자 대시보드 — 본사/지점 공통·본사 전용 카드, 금일 거래·Buy·문의·시세(7일).
 * 레이아웃: 요약 행 → (본사) 회원·지점 1행 → 금일 목록 행 → 시세·문의 행.
 * 금/은 시세: Apache ECharts 스플라인(현물시세 페이지와 동일 계열).
 */
import * as echarts from 'echarts'
import { getDashboard } from '@/api/dashboard.js'

/** 금/은 미니차트: 포인트 위 떠 있는 금액 라벨(graphic id) */
const METAL_PRICE_LABEL_GID = 'dash-metal-price-label'

export default {
  name: 'DashboardPage',
  data() {
    return {
      loading: false,
      dash: null,
      goldChartInstance: null,
      silverChartInstance: null,
    }
  },
  computed: {
    todayStr() {
      const d = new Date()
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
  },
  mounted() {
    window.addEventListener('resize', this.onMetalChartResize)
    this.load()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onMetalChartResize)
    this.disposeMetalCharts()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await getDashboard()
        if (res.data?.code === 0) {
          this.dash = res.data.data || null
        }
      } catch (e) {
        console.error('[DashboardPage] load', e)
      } finally {
        this.loading = false
      }
      await this.$nextTick()
      this.updateMetalCharts()
    },
    onMetalChartResize() {
      this.goldChartInstance?.resize()
      this.silverChartInstance?.resize()
    },
    disposeMetalCharts() {
      if (this.goldChartInstance) {
        this.unbindMetalChartPriceLabel(this.goldChartInstance)
        this.goldChartInstance.dispose()
        this.goldChartInstance = null
      }
      if (this.silverChartInstance) {
        this.unbindMetalChartPriceLabel(this.silverChartInstance)
        this.silverChartInstance.dispose()
        this.silverChartInstance = null
      }
    },
    getOrInitMetalChart(instance, refName) {
      if (instance && !document.contains(instance.getDom())) {
        instance.dispose()
        instance = null
      }
      if (!instance) {
        const dom = this.$refs[refName]
        if (!dom) return null
        instance = echarts.init(dom, null, { renderer: 'canvas' })
      }
      return instance
    },
    /** 시세 차트 축·툴팁용: 월/일만 (예: 3/29) */
    formatChartDateMd(val) {
      if (val == null || val === '') return ''
      const s = String(val).trim()
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (m) return `${Number(m[2])}/${Number(m[3])}`
      const d = new Date(s)
      if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}/${d.getDate()}`
      return s
    },
    /**
     * @param {Array<{ quotedAt: string, priceKrwPerG: number }>} dataArr
     */
    buildMiniMetalChartOption(dataArr, seriesName, lineColor, gradientTop, gradientBottom) {
      if (!dataArr?.length) {
        return {
          backgroundColor: 'transparent',
          graphic: {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: '데이터 없음', fill: '#9E9E9E', fontSize: 13 },
          },
        }
      }
      const labels = dataArr.map((d) => d.quotedAt)
      const values = dataArr.map((d) => d.priceKrwPerG)
      const minVal = Math.min(...values)
      const maxVal = Math.max(...values)
      const range = maxVal - minVal
      const buffer = Math.max(range * 0.3, minVal * 0.005)
      const yMin = Math.floor(minVal - buffer)
      const yMax = Math.ceil(maxVal + buffer)

      return {
        backgroundColor: 'transparent',
        grid: { top: 24, right: 8, bottom: 52, left: 4, containLabel: true },
        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.45)' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 10,
            rotate: 28,
            margin: 8,
            interval: Math.max(0, Math.floor(labels.length / 6) - 1),
            formatter: (v) => this.formatChartDateMd(v),
          },
          splitLine: { show: false },
        },
        yAxis: {
          type: 'value',
          name: '원/g',
          min: yMin,
          max: yMax,
          nameTextStyle: { color: '#9fb0c9', fontSize: 10, align: 'right' },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#a9b8cf',
            fontSize: 9,
            formatter: (val) => val.toLocaleString('ko-KR'),
          },
          splitLine: { lineStyle: { color: 'rgba(148, 163, 184, 0.22)', type: 'dashed' } },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'line',
            snap: true,
            lineStyle: { color: 'rgba(203, 213, 225, 0.85)', width: 1, type: 'dashed' },
          },
          backgroundColor: 'rgba(15, 23, 42, 0.96)',
          borderColor: 'rgba(148, 163, 184, 0.5)',
          borderWidth: 1,
          padding: [8, 12],
          textStyle: { color: '#e5ecf8', fontSize: 11 },
          formatter(params) {
            const p = params[0]
            if (!p || p.value == null) return ''
            const price = Number(p.value).toLocaleString('ko-KR')
            const dateLine = this.formatChartDateMd(p.axisValue)
            return `<div style="font-weight:600;margin-bottom:4px;color:#e5ecf8;">${dateLine}</div>
              <div style="font-size:13px;font-weight:700;color:#f8fafc;">${price}<span style="font-size:11px;font-weight:600;color:#b8c7dc;margin-left:4px;">원/g</span></div>
              <div style="margin-top:2px;color:#9fb0c9;font-size:10px;">${p.seriesName}</div>`
          },
        },
        /** 축 포인터와 convertToPixel로 위치 잡는 금액 라벨용(초기 숨김) */
        graphic: [{
          id: METAL_PRICE_LABEL_GID,
          type: 'text',
          silent: true,
          z: 200,
          invisible: true,
          x: 0,
          y: 0,
          style: {
            text: '',
            textAlign: 'center',
            textVerticalAlign: 'bottom',
            fill: '#f8fafc',
            fontSize: 11,
            fontWeight: 'bold',
            backgroundColor: 'rgba(15, 23, 42, 0.96)',
            padding: [4, 7],
            borderRadius: 4,
            borderWidth: 1,
            borderColor: 'rgba(148, 163, 184, 0.5)',
          },
        }],
        series: [{
          name: seriesName,
          type: 'line',
          smooth: true,
          data: values,
          lineStyle: { color: lineColor, width: 2 },
          symbol: 'circle',
          /** 평소 점 숨김(opacity 0), 축 스냅 시 포인트 강조 — 금액은 graphic으로 표시(emphasis.label은 축 트리거에서 미표시됨) */
          symbolSize: 9,
          showSymbol: true,
          itemStyle: {
            color: lineColor,
            borderColor: '#fff',
            borderWidth: 1.5,
            opacity: 0,
          },
          emphasis: {
            disabled: false,
            scale: 1.15,
            itemStyle: {
              color: lineColor,
              borderColor: '#fff',
              borderWidth: 2,
              opacity: 1,
              shadowBlur: 10,
              shadowColor: 'rgba(0,0,0,0.22)',
            },
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: gradientTop },
              { offset: 1, color: gradientBottom },
            ]),
          },
        }],
      }
    },
    unbindMetalChartPriceLabel(chart) {
      const b = chart?.__dashMetalPriceLabel
      if (!b) return
      chart.off('updateAxisPointer', b.onAxisPtr)
      if (b.dom && b.onDomLeave) {
        b.dom.removeEventListener('mouseleave', b.onDomLeave)
      }
      chart.__dashMetalPriceLabel = null
    },
    hideMetalChartPriceLabel(chart) {
      if (!chart) return
      chart.setOption({
        graphic: [{
          id: METAL_PRICE_LABEL_GID,
          $action: 'merge',
          invisible: true,
          style: { text: '' },
        }],
      })
    },
    /**
     * 축 포인터 위치에 맞춰 포인트 픽셀에 금액 라벨(graphic) 표시
     * @param {*} chart echarts instance
     * @param {string[]} labels
     * @param {number[]} values
     */
    bindMetalChartPriceLabel(chart, labels, values) {
      if (!chart || !labels?.length || !values?.length) return
      this.unbindMetalChartPriceLabel(chart)
      const onAxisPtr = (ev) => {
        if (ev?.currTrigger === 'leave') {
          this.hideMetalChartPriceLabel(chart)
          return
        }
        /** axisTrigger 반환 페이로드: 최상위 dataIndex·axesInfo[].value(스냅된 축값) */
        let di = typeof ev?.dataIndex === 'number' && ev.dataIndex >= 0 ? ev.dataIndex : -1
        const xVal = (ev?.axesInfo || []).find((a) => a.axisDim === 'x')?.value
        if (di < 0 && xVal != null) {
          di = labels.findIndex((l) => String(l) === String(xVal))
        }
        if (di < 0 || di >= values.length) {
          this.hideMetalChartPriceLabel(chart)
          return
        }
        const yVal = values[di]
        const text = `${Number(yVal).toLocaleString('ko-KR')}원/g`
        let pt
        try {
          pt = chart.convertToPixel({ seriesIndex: 0 }, [di, yVal])
        } catch {
          pt = null
        }
        if (!pt || !Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) {
          try {
            pt = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [labels[di], yVal])
          } catch {
            pt = null
          }
        }
        if (!pt || !Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) {
          this.hideMetalChartPriceLabel(chart)
          return
        }
        const offsetY = 14
        chart.setOption({
          graphic: [{
            id: METAL_PRICE_LABEL_GID,
            $action: 'merge',
            invisible: false,
            x: pt[0],
            y: pt[1] - offsetY,
            style: { text },
          }],
        })
      }
      const dom = chart.getDom()
      const onDomLeave = () => this.hideMetalChartPriceLabel(chart)
      chart.on('updateAxisPointer', onAxisPtr)
      dom.addEventListener('mouseleave', onDomLeave)
      chart.__dashMetalPriceLabel = { onAxisPtr, onDomLeave, dom }
    },
    updateMetalCharts() {
      if (!this.dash) {
        this.disposeMetalCharts()
        return
      }
      const gold = this.dash.goldWeek || []
      const silver = this.dash.silverWeek || []

      this.goldChartInstance = this.getOrInitMetalChart(this.goldChartInstance, 'goldChartContainer')
      if (this.goldChartInstance) {
        this.goldChartInstance.setOption(
          this.buildMiniMetalChartOption(
            gold,
            '금',
            '#C2A569',
            'rgba(194,165,105,0.35)',
            'rgba(194,165,105,0.03)',
          ),
          true,
        )
        if (gold.length) {
          this.bindMetalChartPriceLabel(
            this.goldChartInstance,
            gold.map((d) => d.quotedAt),
            gold.map((d) => d.priceKrwPerG),
          )
        } else {
          this.unbindMetalChartPriceLabel(this.goldChartInstance)
        }
      }

      this.silverChartInstance = this.getOrInitMetalChart(this.silverChartInstance, 'silverChartContainer')
      if (this.silverChartInstance) {
        this.silverChartInstance.setOption(
          this.buildMiniMetalChartOption(
            silver,
            '은',
            '#A8A9AD',
            'rgba(168,169,173,0.25)',
            'rgba(168,169,173,0.03)',
          ),
          true,
        )
        if (silver.length) {
          this.bindMetalChartPriceLabel(
            this.silverChartInstance,
            silver.map((d) => d.quotedAt),
            silver.map((d) => d.priceKrwPerG),
          )
        } else {
          this.unbindMetalChartPriceLabel(this.silverChartInstance)
        }
      }

      this.$nextTick(() => {
        this.goldChartInstance?.resize()
        this.silverChartInstance?.resize()
      })
    },
    fmtNum(n) {
      if (n == null || n === '') return '0'
      return new Intl.NumberFormat('ko-KR').format(Number(n))
    },
    fmtWon(n) {
      if (n == null || n === '') return '-'
      return `${new Intl.NumberFormat('ko-KR').format(Number(n))}원`
    },
    spotLabel(s) {
      if (s === 'GOLD') return '금'
      if (s === 'SILVER') return '은'
      return s || '-'
    },
    formatDt(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
    tradeTodayQuery(tradeSide) {
      return {
        path: '/trade',
        query: {
          startDate: this.todayStr,
          endDate: this.todayStr,
          tradeSide: tradeSide || undefined,
        },
      }
    },
    settlementBuyTodayQuery() {
      return {
        path: '/settlement',
        query: {
          startDate: this.todayStr,
          endDate: this.todayStr,
          tradeKind: 'PRODUCT_BUY',
        },
      }
    },
    goSettlement(row) {
      if (row.tradeUnifiedLedgerCode) {
        this.$router.push(`/settlement/${row.tradeUnifiedLedgerCode}`)
      }
    },
    goMemberFromInquiry(row) {
      if (row.userCode) this.$router.push(`/members/${row.userCode}`)
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
      <div>
        <div class="text-h5 font-weight-bold mb-1">대시보드</div>
        <div class="text-body-2 text-grey">금일 기준 현황 요약 · 본사/지점 범위는 로그인 권한에 따릅니다.</div>
      </div>
      <v-btn variant="tonal" color="primary" size="small" :loading="loading" @click="load">
        새로고침
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="mb-4" />

    <template v-if="dash">
      <!-- Row 1: 요약 카드 (가로 배치, 줄바꿈) -->
      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">요약</div>
      <v-row class="mb-8">
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-title class="text-subtitle-1 font-weight-bold">금일 호가거래</v-card-title>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>금 · 건수</span><span>{{ fmtNum(dash.p2pToday?.goldTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>금 · 금액</span><span>{{ fmtWon(dash.p2pToday?.goldAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between"><span>은 · 건수</span><span>{{ fmtNum(dash.p2pToday?.silverTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>은 · 금액</span><span>{{ fmtWon(dash.p2pToday?.silverAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 건수</span><span>{{ fmtNum(dash.p2pToday?.totalTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 금액</span><span>{{ fmtWon(dash.p2pToday?.totalAmountSum) }}</span></div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-title class="text-subtitle-1 font-weight-bold">금일 상품구매(Buy)</v-card-title>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>금 · 건수</span><span>{{ fmtNum(dash.productBuyToday?.goldTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>금 · 금액</span><span>{{ fmtWon(dash.productBuyToday?.goldAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between"><span>은 · 건수</span><span>{{ fmtNum(dash.productBuyToday?.silverTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between"><span>은 · 금액</span><span>{{ fmtWon(dash.productBuyToday?.silverAmountSum) }}</span></div>
              <v-divider class="my-2" />
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 건수</span><span>{{ fmtNum(dash.productBuyToday?.totalTradeCount) }}건</span></div>
              <div class="d-flex justify-space-between font-weight-bold"><span>합계 금액</span><span>{{ fmtWon(dash.productBuyToday?.totalAmountSum) }}</span></div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 감정</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/appraisal">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>접수·등록(금일)</span><span>{{ fmtNum(dash.appraisalToday?.totalToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>대기·진행</span><span>{{ fmtNum(dash.appraisalToday?.waitingToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>완료(금일)</span><span>{{ fmtNum(dash.appraisalToday?.completedToday) }}건</span></div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="3">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 문의(Q&amp;A)</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/inquiries">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="text-body-2">
              <div class="d-flex justify-space-between"><span>질문(금일)</span><span>{{ fmtNum(dash.inquiryToday?.questionCountToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>답변(금일)</span><span>{{ fmtNum(dash.inquiryToday?.answerCountToday) }}건</span></div>
              <div class="d-flex justify-space-between"><span>미답변 스레드</span><span>{{ fmtNum(dash.inquiryToday?.unansweredOpenCount) }}건</span></div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- 본사 전용: 회원 · 지점 · 금일 입·출금 (lg 이상 4열) -->
      <template v-if="dash.hq && (dash.membersHq || dash.branchesHq || dash.cashTodayHq)">
        <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">본사</div>
        <v-row class="mb-8">
          <v-col v-if="dash.membersHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100">
              <v-card-item>
                <v-card-title class="text-subtitle-1 font-weight-bold">회원</v-card-title>
                <template #append>
                  <v-btn variant="text" size="small" color="primary" to="/members">전체보기</v-btn>
                </template>
              </v-card-item>
              <v-card-text class="text-body-2">
                <div class="d-flex justify-space-between"><span>총 회원</span><span>{{ fmtNum(dash.membersHq.totalMembers) }}명</span></div>
                <div class="d-flex justify-space-between"><span>금일 가입</span><span>{{ fmtNum(dash.membersHq.joinedToday) }}명</span></div>
                <div class="d-flex justify-space-between"><span>금주 가입</span><span>{{ fmtNum(dash.membersHq.joinedThisWeek) }}명</span></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="dash.branchesHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100">
              <v-card-item>
                <v-card-title class="text-subtitle-1 font-weight-bold">지점</v-card-title>
                <template #append>
                  <v-btn variant="text" size="small" color="primary" to="/branches">전체보기</v-btn>
                </template>
              </v-card-item>
              <v-card-text class="text-body-2">
                <div class="d-flex justify-space-between"><span>총 지점</span><span>{{ fmtNum(dash.branchesHq.totalBranches) }}개</span></div>
                <div class="d-flex justify-space-between"><span>신규(최근 30일)</span><span>{{ fmtNum(dash.branchesHq.newBranchesLast30Days) }}개</span></div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="dash.cashTodayHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100 dashboard-hq-cash dashboard-hq-cash--in">
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 가상계좌 입금</v-card-title>
              <v-card-text class="text-body-2">
                <div class="text-h6 font-weight-bold text-success mb-2">
                  {{ fmtWon(dash.cashTodayHq.depositAmountToday) }}
                </div>
                <div class="d-flex justify-space-between">
                  <span class="text-grey">건수</span>
                  <span>{{ fmtNum(dash.cashTodayHq.depositCountToday) }}건</span>
                </div>
                <div class="text-caption text-grey mt-2">
                  ledger_cash · 가상계좌 유입(VIRTUAL_ACCOUNT_IN / BANK_TOPUP)
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="dash.cashTodayHq" cols="12" sm="6" lg="3">
            <v-card rounded="lg" elevation="1" class="h-100 dashboard-hq-cash dashboard-hq-cash--out">
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 출금 이체</v-card-title>
              <v-card-text class="text-body-2">
                <div class="text-h6 font-weight-bold text-error mb-2">
                  {{ fmtWon(dash.cashTodayHq.withdrawAmountToday) }}
                </div>
                <div class="d-flex justify-space-between">
                  <span class="text-grey">완료 건수</span>
                  <span>{{ fmtNum(dash.cashTodayHq.withdrawCountToday) }}건</span>
                </div>
                <div class="text-caption text-grey mt-2">
                  출금요청(COMPLETED) · 회원 계좌 이체 완료일(complete_dt) 기준, 이체 금액(amount)
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Row 2: 금일 거래 목록 (한 줄에 3개, 모바일은 세로) -->
      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">금일 거래 내역</div>
      <v-row class="mb-8">
        <v-col cols="12" lg="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 호가 · 매수</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" :to="tradeTodayQuery('BUY')">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>현물</th>
                  <th class="text-end">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in (dash.p2pBuyTodayList || [])"
                  :key="row.tradeUnifiedLedgerCode"
                  style="cursor:pointer"
                  @click="goSettlement(row)"
                >
                  <td class="text-caption">{{ formatDt(row.occurredAt) }}</td>
                  <td>{{ spotLabel(row.spotType) }}</td>
                  <td class="text-end text-caption">{{ fmtWon(row.tradeAmount) }}</td>
                </tr>
                <tr v-if="!(dash.p2pBuyTodayList || []).length">
                  <td colspan="3" class="text-center text-grey py-4">내역 없음</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
        <v-col cols="12" lg="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 호가 · 매도</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" :to="tradeTodayQuery('SELL')">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>현물</th>
                  <th class="text-end">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in (dash.p2pSellTodayList || [])"
                  :key="row.tradeUnifiedLedgerCode"
                  style="cursor:pointer"
                  @click="goSettlement(row)"
                >
                  <td class="text-caption">{{ formatDt(row.occurredAt) }}</td>
                  <td>{{ spotLabel(row.spotType) }}</td>
                  <td class="text-end text-caption">{{ fmtWon(row.tradeAmount) }}</td>
                </tr>
                <tr v-if="!(dash.p2pSellTodayList || []).length">
                  <td colspan="3" class="text-center text-grey py-4">내역 없음</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
        <v-col cols="12" lg="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금일 상품구매(Buy)</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" :to="settlementBuyTodayQuery()">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>일시</th>
                  <th>현물</th>
                  <th class="text-end">금액</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in (dash.productBuyTodayList || [])"
                  :key="row.tradeUnifiedLedgerCode"
                  style="cursor:pointer"
                  @click="goSettlement(row)"
                >
                  <td class="text-caption">{{ formatDt(row.occurredAt) }}</td>
                  <td>{{ spotLabel(row.spotType) }}</td>
                  <td class="text-end text-caption">{{ fmtWon(row.tradeAmount) }}</td>
                </tr>
                <tr v-if="!(dash.productBuyTodayList || []).length">
                  <td colspan="3" class="text-center text-grey py-4">내역 없음</td>
                </tr>
              </tbody>
            </v-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 3: 시세 · 최근 문의 -->
      <div class="text-subtitle-2 font-weight-bold text-grey-darken-1 mb-3">시세 · 문의</div>
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">금 시세 (최근 7일)</v-card-title>
              <template v-if="dash.hq" #append>
                <v-btn variant="text" size="small" color="primary" to="/metal-price">상세</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="pa-2">
              <div ref="goldChartContainer" class="dashboard-metal-chart" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">은 시세 (최근 7일)</v-card-title>
              <template v-if="dash.hq" #append>
                <v-btn variant="text" size="small" color="primary" to="/metal-price">상세</v-btn>
              </template>
            </v-card-item>
            <v-card-text class="pa-2">
              <div ref="silverChartContainer" class="dashboard-metal-chart" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card rounded="lg" elevation="1" class="h-100">
            <v-card-item>
              <v-card-title class="text-subtitle-1 font-weight-bold">최근 문의</v-card-title>
              <template #append>
                <v-btn variant="text" size="small" color="primary" to="/inquiries">전체보기</v-btn>
              </template>
            </v-card-item>
            <v-list density="compact" lines="two" class="dashboard-inquiry-list">
              <v-list-item
                v-for="row in (dash.recentInquiries || [])"
                :key="row.boardCode + row.userCode"
                :title="row.userName || row.userCode"
                :subtitle="(row.boardTitle || '') + (row.previewText ? ' — ' + row.previewText : '')"
                @click="goMemberFromInquiry(row)"
              >
                <template #append>
                  <span class="text-caption text-grey">{{ formatDt(row.lastQuestionAt) }}</span>
                </template>
              </v-list-item>
              <v-list-item v-if="!(dash.recentInquiries || []).length">
                <v-list-item-title class="text-grey">문의 없음</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-alert v-else-if="!loading" type="info" variant="tonal" class="mt-4">
      대시보드 데이터를 불러오지 못했습니다. 네트워크·로그인 상태를 확인해 주세요.
    </v-alert>
  </div>
</template>

<style scoped>
.dashboard-metal-chart {
  width: 100%;
  height: 240px;
  min-height: 200px;
}
.dashboard-hq-cash {
  border-inline-start: 3px solid #e0e0e0;
}
.dashboard-hq-cash--in {
  border-inline-start-color: #2e7d32;
}
.dashboard-hq-cash--out {
  border-inline-start-color: #c62828;
}

.dashboard-inquiry-list {
  background: transparent !important;
}

.dashboard-inquiry-list :deep(.v-list-item) {
  background: transparent !important;
  border-bottom: 1px solid rgba(71, 85, 105, 0.22);
}

.dashboard-inquiry-list :deep(.v-list-item:last-child) {
  border-bottom: 0;
}

.dashboard-inquiry-list :deep(.v-list-item-title) {
  color: #e5e7eb !important;
  font-weight: 600;
}

.dashboard-inquiry-list :deep(.v-list-item-subtitle) {
  color: #94a3b8 !important;
}
</style>
